import type {
  TrackEventArguments,
  TrackPageOptions,
} from '../../thirdPartyTrackingAgent/types';

interface TrackPageArgs {
  page: string;
  options?: TrackPageOptions;
}

interface GoogleStateParameters {
  [key: string]: unknown;
  hasThePageBeenTracked?: boolean;
  lastTrackedPage?: string;
  hasSetDefaultCustomDimensions?: boolean;
  eventsQueue?: TrackEventArguments[];
  pagesQueue?: TrackPageArgs[];
  trackPageFn?: (page: string, options?: TrackPageOptions) => Promise<void>;
}

export interface RuntimeGoogleState {
  getGoogleState: () => GoogleStateParameters;
  getEventsQueue: () => TrackEventArguments[];
  getPagesQueue: () => TrackPageArgs[];
  setParameters: (params?: Partial<GoogleStateParameters>) => void;
  queueEvent: (event: TrackEventArguments) => void;
  queuePage: (page: TrackPageArgs) => void;
  emptyEventsQueue: () => void;
  emptyPagesQueue: () => void;
  registerTrackPage: (
    fn: (page: string, options?: TrackPageOptions) => Promise<void>
  ) => void;
  getTrackPageFn: () =>
    | ((page: string, options?: TrackPageOptions) => Promise<void>)
    | undefined;
}

let runtimeGoogleState: RuntimeGoogleState | null = null;

export const createRuntimeGoogleState = (): RuntimeGoogleState => {
  if (runtimeGoogleState) {
    return runtimeGoogleState;
  }

  let currentState: GoogleStateParameters = {};

  const setParameters = (configParams: Partial<GoogleStateParameters>) => {
    currentState = { ...currentState, ...configParams };
  };

  const queueEvent = (event: TrackEventArguments) => {
    currentState.eventsQueue = currentState.eventsQueue || [];
    currentState.eventsQueue.push(event);
  };

  const queuePage = (page: TrackPageArgs) => {
    currentState.pagesQueue = currentState.pagesQueue || [];
    currentState.pagesQueue.push(page);
  };

  const emptyEventsQueue = () => {
    currentState.eventsQueue = [];
  };
  const emptyPagesQueue = () => {
    currentState.pagesQueue = [];
  };

  const getGoogleState = () => currentState;

  const getEventsQueue = () => currentState.eventsQueue || [];

  const getPagesQueue = () => currentState.pagesQueue || [];

  const registerTrackPage = (
    fn: (page: string, options?: TrackPageOptions) => Promise<void>
  ) => {
    currentState.trackPageFn = fn;
  };

  const getTrackPageFn = () =>
    currentState.trackPageFn as
      | ((page: string, options?: TrackPageOptions) => Promise<void>)
      | undefined;

  runtimeGoogleState = {
    setParameters,
    getGoogleState,
    getEventsQueue,
    getPagesQueue,
    queueEvent,
    queuePage,
    emptyEventsQueue,
    emptyPagesQueue,
    registerTrackPage,
    getTrackPageFn,
  };

  return runtimeGoogleState;
};

export const resetRuntimeGoogleState = () => {
  runtimeGoogleState = null;
};
